import React, { useMemo } from 'react'
import dayjs from 'dayjs'
import { useTranslation } from 'react-i18next'
import { Box, LineChart, RowsSkeletonLoader } from '@node-space/ui-components'
import { DateFormat } from 'constants/General'
import { LedgerWalletTransaction } from 'types/ledger/ledgerWalletTransactions'
import { calculateChartTicks } from '../utils'

interface RewardsCryptoPerformanceProps {
  currency: string
  loading: boolean
  transactions: LedgerWalletTransaction[]
}

export const RewardsCryptoPerformance = ({
  currency,
  loading,
  transactions,
}: RewardsCryptoPerformanceProps) => {
  const { t } = useTranslation()
  const chartData = useMemo(() => {
    let totalValue = 0
    return transactions.map(transaction => {
      if (transaction.amount.value > 0) {
        totalValue += transaction.amount.value
      } else {
        totalValue -= Math.abs(transaction.amount.value)
      }

      return {
        date: dayjs(transaction.createdAt).format(DateFormat.DD_MMM_YYYY),
        value: totalValue,
      }
    })
  }, [transactions])

  const { axisTicks, tickCount, decimals } = calculateChartTicks(chartData.map(d => d.value))

  if (loading) {
    return <RowsSkeletonLoader rows={5} />
  }

  return (
    <Box flexCol gap={16}>
      <h4 className="text-h4 text-foreground-prominent">
        {t('rewards.performance', {
          currency,
        })}
      </h4>
      <Box className="ml-[-20px]">
        <LineChart
          lineType="step"
          data={chartData}
          dataKey="value"
          height={300}
          xAxisProps={{ dataKey: 'date' }}
          yAxisProps={{ axisTicks, axisTickCount: tickCount, axisPrecision: decimals }}
        />
      </Box>
    </Box>
  )
}
